import { Button, ComboBox, ExportLimitsStatus, Select, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { SITE_DATA_LAYER_ID_PREFIX } from '@civilgrid/utils/src/extract/constants';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type ButtonHTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { proxySet } from 'valtio/utils';
import {
  type ExtractProjection,
  type ExtractTemplateOption,
  getDefaultExtractProjection,
  getExtractProjections,
  getOrganizationExtractTemplates,
  submitProjectExtract,
} from '~/api/extract';
import { listProjectCustomData } from '~/api/projectCustomData';
import { hasFeatureAccess } from '~/authorizations/FeatureFlags';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import type { ExportEntryPoint } from '~/common/posthogEvents';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useQuery as useSearchParams } from '~/common/routerUtils';
import { defaultBasemapStyle, MapStyle } from '~/common/types';
import Icon from '~/components2/Icon';
import InfoTooltip from '~/components2/InfoTooltip';
import Notify from '~/components2/Notify';
import Popover from '~/components2/Popover/Popover';
import { DataUseAcknowledgement, SendEmailModal } from '~/components2/SendEmailModal';
import { calculateArea } from '~/helpers/unitHelpers';
import { useExportCustomDataEnabled } from '~/hooks/useExportCustomDataEnabled';
import { useExportEnableAsyncLimits } from '~/hooks/useExportEnableAsyncLimits';
import { useExportEnableLayerPickerV2 } from '~/hooks/useExportEnableLayerPickerV2';
import { useExportMultiFormatEnabled } from '~/hooks/useExportMultiFormatEnabled';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import { usePotholeFieldNoteExportEnabled } from '~/hooks/usePotholeFieldNoteExportEnabled';
import { useSiteDataLayerEnabled } from '~/hooks/useSiteDataLayerEnabled';
import { ConfidentialityNotice } from '~/pages/common/ConfidentialityNotice';
// Shared cache key + poll interval with the map/files custom-data panel (CustomDataLayers.tsx
// imports the same module the same way).
import { customDataQueryKey, customDataRefetchInterval } from '~/pages/projectDetails/filesAndMedia/CustomDataSection';
import { useListGeoItems } from '~/pages/projectDetails/potholing/common';
import rootStore from '~/store';
import { layerIdToDownloadLayer, layerIdToDownloadLayerOverrides } from '~/store2/downloadLayers';
import layerStore from '~/store2/layers';
import { TOCLabelMap } from '~/store2/TOCLayers';
import type { ProjectDetailsParams } from '../..';
import {
  EXPORT_ENTRY_POINT_PARAM,
  EXPORT_HISTORY_TAB_ID,
  UTILITIES_EXPORTS_MENU_ENTRY_POINT,
} from '../../exportTabIds';
import projectDetailsMapStore from '../../map/projectDetailsMapStore';
// Reuse the existing download form's store + layer picker. They already manage the raw
// layer IDs (no ESRI transform — the extract backend maps them), the map preview wiring,
// and setup/reset. We submit Array.from(selectedLayerIds) straight to the extract endpoint.
import { SubmitRequestDialog } from '../../new-download';
import { getSelectedLayersText } from '../../new-download/common';
import { DownloadLayerPicker, LAYER_SEARCH_INPUT_ID } from '../../new-download/layerPicker';
import { NoLayersWarning, RestrictionInfo } from '../../new-download/RestrictionInfo';
import newDownloadStore from '../../new-download/store';
import type { ProjectDownload } from '../common';
import ExportHistory from '../ExportHistory';
import {
  reportExportFormDataLoadFailure,
  useExportTabLoadTelemetry,
  useReportCustomDataLoadFailure,
} from '../loadTelemetry';
import downloadStore from '../store';
import { useLoadExportHistory } from '../useLoadExportHistory';
import {
  buildExportFailedProperties,
  buildExportInitiatedProperties,
  buildProjectExtractRequest,
  chooseInitialProjectionId,
  DEFAULT_TEMPLATE_OPTION,
  describeDroppedLayers,
  ExtractFormat,
  formatAreaAcres,
  formatSelectionLabel,
  getProjectionDescription,
  getSubmissionBlock,
  getSubmissionBlockExpiry,
  isExtractDownloadDisabled,
  isProjectionFieldVisible,
  isProjectionPickerEnabled,
  isTemplateFieldVisible,
  MAX_DIRECT_DOWNLOAD_ACRES,
  projectionSelectValue,
  resetFormatsWhenMultiFormatEnabled,
  shouldShowManualRequestFallback,
  type TExtractFormat,
  templateIdFromOption,
  templatePickerOptions,
} from './common';
import { pruneStaleSelections, resolveCustomDataPickerState } from './customLayers';
import { FormatPicker } from './FormatPicker';
import {
  buildFormRebuiltProperties,
  labelForRebuiltLayerId,
  newestRunForProject,
  rebuildFromRun,
} from './rebuildLastExport';
import { buildSiteDataPickerSection, getSiteDataAvailability } from './siteDataLayers';

/** Map content for the FME extract tab — none today (the form needs no map overlay). */
export function MapChildren() {
  return null;
}

/** The FME extract download form: pick format, template, layers, and projection, then submit. */
export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();
  const labels = useExportLabels();
  // The org-templates effect below is keyed on currentOrganizationId, not projectId, so a
  // still-in-flight fetch that fails after a same-org project switch would otherwise report the
  // project that effect last ran for, not the one the user is now on.
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const { currentOrganizationId, preferredBasemap, currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const { project, selectedLayerIds, restrictedLayerIds, isCheckingLayerAssets, limitsStatus } = useSnapshot(
    newDownloadStore,
  ) as typeof newDownloadStore;
  const { lastSubmission, downloads } = useSnapshot(downloadStore) as typeof downloadStore;

  const multiFormatEnabled = useExportMultiFormatEnabled();
  const pickerV2Enabled = useExportEnableLayerPickerV2();
  // Read once: the load it picks starts on mount, so a flag that resolves later applies on the next open
  const asyncLimits = useRef(useExportEnableAsyncLimits()).current;
  const { resolved: exportCustomDataResolved, enabled: exportCustomDataEnabled } = useExportCustomDataEnabled();
  const [formats, setFormats] = useState<TExtractFormat[]>([ExtractFormat.DWG]);
  // export-multi-format-enabled resolves after the initial render, so the empty-by-default
  // format selection can't live in the useState initializer above — this clears the untouched
  // single-format default once the flag resolves true.
  useEffect(() => {
    setFormats((current) => resetFormatsWhenMultiFormatEnabled(multiFormatEnabled, current));
  }, [multiFormatEnabled]);
  const [projections, setProjections] = useState<ExtractProjection[]>([]);
  const [projectionId, setProjectionId] = useState<string | undefined>();
  const [templates, setTemplates] = useState<ExtractTemplateOption[]>([]);
  const [extractTemplateId, setExtractTemplateId] = useState<string | null>(null);
  const [downloadArea, setDownloadArea] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isLoadingProjections, setIsLoadingProjections] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const submitInFlight = useRef(false);
  // Re-read the clock each second so the cooldown tooltip counts down and the button re-enables.
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());

  const containerDiv = useRef<HTMLDivElement>(null);
  const { projectDetailsMap } = useMap();

  const isDownloadEnabled =
    !!project && hasFeatureAccess(ProjectFeature.Downloads, project.billingTier, currentOrganization?.subscription);
  const formLoading = loading || isLoadingProjections || isLoadingTemplates;
  const formVisible = !formLoading && isDownloadEnabled;
  // How the user reached this tab, read once on arrival.
  const searchParams = useSearchParams();
  const [entryPoint] = useState<ExportEntryPoint>(() =>
    searchParams.get(EXPORT_ENTRY_POINT_PARAM) === UTILITIES_EXPORTS_MENU_ENTRY_POINT
      ? 'utilities_exports_menu'
      : 'other',
  );
  const reportExportTabLoaded = useExportTabLoadTelemetry(projectId, formVisible, entryPoint);

  // Same query key + poll interval as the map/files panel — the cache is shared, so this is
  // usually warm. The query stays unconditional; export-custom-data-enabled gates only what this
  // picker offers.
  const {
    data: customDataRows,
    isSuccess: customDataResolved,
    error: customDataError,
  } = useQuery({
    queryKey: customDataQueryKey(projectId),
    queryFn: () => listProjectCustomData(projectId),
    enabled: Boolean(projectId),
    refetchOnWindowFocus: false,
    refetchInterval: customDataRefetchInterval,
    retry: false,
  });
  const { section: customDataSection } = resolveCustomDataPickerState({
    flagResolved: exportCustomDataResolved,
    flagEnabled: exportCustomDataEnabled,
    rows: customDataRows,
    rowsResolved: customDataResolved,
    selectedLayerIds,
  });
  const potholeFieldNoteExportEnabled = usePotholeFieldNoteExportEnabled();
  const siteDataLayerEnabled = useSiteDataLayerEnabled();
  // Shares the map TOC's `list-geo-items` cache, so this is usually warm.
  const { data: siteDataGeoItems } = useListGeoItems({
    projectId,
    enabled: Boolean(projectId) && potholeFieldNoteExportEnabled && siteDataLayerEnabled,
  });
  // Tier + per-org featureAccess override, matching the extract submit endpoint's own check —
  // absent sections are dropped.
  const siteDataSection = buildSiteDataPickerSection({
    billingTier: project?.billingTier,
    exportEnabled: potholeFieldNoteExportEnabled,
    siteDataLayerEnabled,
    subscription: currentOrganization?.subscription,
    availability: getSiteDataAvailability(
      siteDataGeoItems,
      !!currentOrganization,
      currentOrganization?.mobileReportType,
    ),
  });
  const extraSections = [customDataSection, siteDataSection].filter((section) => section !== undefined);
  const offeredSiteDataIds = (siteDataSection?.children ?? []).map((child) => child.id);
  // A stable key for the effect below: the leaves themselves are fixed, only which are offered changes.
  const offeredSiteDataKey = offeredSiteDataIds.join(',');

  useReportCustomDataLoadFailure(projectId, 'form', customDataError);

  // Rebuild Last Export reads the history ExportHistory loads — but ExportHistory mounts only with
  // multi-format on (below), so without it the form fetches for itself.
  useLoadExportHistory(projectId, pickerV2Enabled && !multiFormatEnabled);

  const lastRun = useMemo(() => newestRunForProject(downloads as ProjectDownload[], projectId), [downloads, projectId]);

  const handleRebuildLastExport = () => {
    if (!lastRun) return;

    const rebuilt = rebuildFromRun(lastRun, {
      offeredLayerIds: newDownloadStore.collectLayerIds([...newDownloadStore.TOCLayers, ...extraSections]),
      projections,
      templates,
      multiFormat: multiFormatEnabled,
      layerMaps: { layerIdToDownloadLayer, overrides: layerIdToDownloadLayerOverrides },
    });

    newDownloadStore.setSelectedLayers(rebuilt.layerIds);
    if (rebuilt.formats) setFormats(rebuilt.formats);
    if (rebuilt.projectionId) setProjectionId(rebuilt.projectionId);
    setExtractTemplateId(rebuilt.extractTemplateId);

    captureTypedPosthogEvent(
      POSTHOG_EVENTS.exportFormRebuilt,
      buildFormRebuiltProperties({ projectId, organizationId: currentOrganizationId, run: lastRun, rebuilt }),
    );

    const dropped = describeDroppedLayers(rebuilt.droppedLayerIds, (layerId) =>
      labelForRebuiltLayerId(layerId, { customDataRows, tocLabels: TOCLabelMap }),
    );
    const projectionName = lastRun.projection?.title;
    const notices = [
      dropped && labels.rebuildDroppedLayersNotice(dropped),
      !rebuilt.projectionId && projectionName && labels.rebuildProjectionUnavailable(projectionName),
      !rebuilt.templateRestored && labels.rebuildTemplateUnavailable,
    ].filter(Boolean);

    if (notices.length > 0) {
      Notify.warning({ message: <span>{notices.join(' ')}</span>, duration: 7000 });
    }
  };

  // fetch project + active projections, setup store; cleanup on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: mirrors new-download lifecycle
  useEffect(() => {
    setLoading(true);

    (async () => {
      await newDownloadStore.getProject(projectId, { asyncLimits });
      setLoading(false);
    })();

    newDownloadStore.setup();

    return () => {
      // CG-752: newDownloadStore.reset() wipes the download selection and synchronously
      // restores the pre-download layer snapshot — no deferred wipe needed here.
      newDownloadStore.reset();
      projectDetailsMapStore.reset();
    };
  }, []);

  // An admin can archive or hide a custom layer mid-session (the query polls), and the export flag
  // can flip under the user: either way a selection left behind would 403 the all-or-nothing submit
  // with no way to untick it. An unresolved flag prunes nothing — that state means "not known yet",
  // not "off".
  // MUST be declared AFTER the setup effect: setup() clears availableCustomLayerIds, and with a
  // warm query cache this effect fires on mount — declared earlier, its publish would be wiped
  // and never re-run (the cached rows reference doesn't change).
  useEffect(() => {
    const { availableIds, staleIds } = resolveCustomDataPickerState({
      flagResolved: exportCustomDataResolved,
      flagEnabled: exportCustomDataEnabled,
      rows: customDataRows,
      rowsResolved: customDataResolved,
      selectedLayerIds: newDownloadStore.selectedLayerIds,
    });
    // Publish what the picker offers so the lightning menu's "Turn On All Layers" can include
    // custom layers (they never live in TOCLayers).
    if (availableIds !== undefined) {
      newDownloadStore.availableCustomLayerIds = proxySet(availableIds);
    }
    if (staleIds.length === 0) return;

    pruneStaleSelections(staleIds, newDownloadStore.selectedLayerIds, layerStore.visibleLayerIds);
  }, [customDataRows, customDataResolved, exportCustomDataResolved, exportCustomDataEnabled]);

  // Publish what the Site Data section offers so the lightning menu's "Turn On All Layers" can
  // include it — these leaves never live in TOCLayers. Declared AFTER the setup effect, which
  // clears the set (same ordering requirement as the custom-data effect above).
  // biome-ignore lint/correctness/useExhaustiveDependencies: offeredSiteDataKey stands in for offeredSiteDataIds
  useEffect(() => {
    newDownloadStore.availableSiteDataLayerIds = proxySet(offeredSiteDataIds);
    // Drop site-data ids that left the picker; leftovers 403 submit and stay on the preview map.
    const offered = new Set(offeredSiteDataIds);
    const staleIds = Array.from(newDownloadStore.selectedLayerIds).filter(
      (id) => id.startsWith(SITE_DATA_LAYER_ID_PREFIX) && !offered.has(id),
    );
    if (staleIds.length === 0) return;
    pruneStaleSelections(staleIds, newDownloadStore.selectedLayerIds, layerStore.visibleLayerIds);
  }, [offeredSiteDataKey]);

  // load projections for this project (scoped to its coverage area on the backend)
  useEffect(() => {
    (async () => {
      try {
        setIsLoadingProjections(true);
        // Fetch the projection list and the project's best-fit projection in parallel.
        const [list, best] = await Promise.all([
          getExtractProjections(projectId),
          getDefaultExtractProjection(projectId),
        ]);
        setProjections(list);
        // Pre-select the server's best-fit (nearest intersecting zone) when available, otherwise
        // fall back to Web Mercator (EPSG:3857) so an extract can't default to the wrong spatial
        // reference. Leave unset if neither is available.
        const initialProjectionId = chooseInitialProjectionId(best, list);
        if (initialProjectionId) setProjectionId(initialProjectionId);
      } catch (e) {
        console.error('error fetching extract projections', e);
        reportExportFormDataLoadFailure(projectId, 'projections', e);
        Notify.error({
          title: 'Get Projections',
          message: 'An error occurred while fetching available projections. Please contact support.',
        });
      } finally {
        setIsLoadingProjections(false);
      }
    })();
  }, [projectId]);

  // load org extract templates when organization changes
  useEffect(() => {
    // Clear any stale options from the previous organization right away so they
    // can't linger while the new org's templates load.
    setTemplates([]);
    setExtractTemplateId(null);

    if (!currentOrganizationId) return;

    // Guard against out-of-order responses: only the latest organization's
    // request is allowed to update state.
    let active = true;

    (async () => {
      try {
        setIsLoadingTemplates(true);
        const list = await getOrganizationExtractTemplates(currentOrganizationId);
        if (!active) return;
        setTemplates(list);
      } catch (e) {
        if (!active) return;
        console.error('error fetching organization extract templates', e);
        reportExportFormDataLoadFailure(projectIdRef.current, 'templates', e);
        Notify.error({
          title: 'Get Extract Templates',
          message: 'An error occurred while fetching available extract templates. Please contact support.',
        });
      } finally {
        if (active) setIsLoadingTemplates(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [currentOrganizationId]);

  // compute project area for the layer picker section labels
  useEffect(() => {
    if (project?.geometry) {
      const projectArea = calculateArea({ unitType: 'acres', unitSystem: 'imperial', geometry: project.geometry });
      setDownloadArea(projectArea.value);
    }
  }, [project]);

  // The preview basemap no longer follows the file format: a submission can carry several formats at
  // once, so there is no single format to key the style off. Re-pins on `preferredBasemap` because
  // the effect below restores that value on cleanup whenever the user picks a different basemap.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mirrors new-download preview wiring
  useEffect(() => {
    const timer = setTimeout(() => {
      projectDetailsMap?.getMap().setStyle(MapStyle.civilGridLight);
    }, 0);

    return () => clearTimeout(timer);
  }, [preferredBasemap]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mirrors new-download preview wiring
  useEffect(() => {
    return () => {
      projectDetailsMap?.getMap().setStyle(preferredBasemap ?? defaultBasemapStyle);
    };
  }, [preferredBasemap]);

  // Ticks the countdown once a second, then once more as the cooldown lapses so the button
  // re-enables on the second it expires.
  useEffect(() => {
    const expiresAt = getSubmissionBlockExpiry(lastSubmission, projectId);
    if (expiresAt === null) return;

    const expiresIn = expiresAt - Date.now();
    // A cooldown that lapsed while the form was on another project has no timer left to clear its
    // stale clock, which would hold the button disabled for good.
    if (expiresIn <= 0) {
      setCooldownNow(Date.now());
      return;
    }

    const tick = () => setCooldownNow(Date.now());
    const countdown = setInterval(tick, 1000);
    const countdownStop = setTimeout(() => {
      clearInterval(countdown);
      tick();
    }, expiresIn);

    return () => {
      clearInterval(countdown);
      clearTimeout(countdownStop);
    };
  }, [lastSubmission, projectId]);

  // Mirror the legacy tab's restriction gating: direct download is blocked for
  // oversized projects or when any selected layer is partner-restricted; the user
  // falls back to a manual download request in that case.
  const hasRestrictedLayer = Array.from(selectedLayerIds).some((layerId) => restrictedLayerIds.has(layerId));
  const limitsUnknown = asyncLimits && limitsStatus !== 'loaded';
  // An oversized project needs a request whatever its limits, so it doesn't wait on them
  const limitsChecking =
    asyncLimits && (limitsStatus === 'loading' || limitsStatus === 'idle') && downloadArea <= MAX_DIRECT_DOWNLOAD_ACRES;
  const isDownloadDisabled = isExtractDownloadDisabled({
    downloadArea,
    selectedLayerCount: selectedLayerIds.size,
    projectionId,
    hasRestrictedLayer,
    formats,
    limitsUnknown,
  });
  const selectedProjectionLabel = projections.find((p) => p.id === projectionId)?.label ?? '';
  // Export and manual-request are mutually exclusive: when review is required, the request is the
  // only path offered.
  const showManualRequestFallback = shouldShowManualRequestFallback({
    isDownloadDisabled,
    selectedLayerCount: selectedLayerIds.size,
    projectionId,
    formats,
    limitsChecking,
  });

  const retryLimits = () => {
    captureTypedPosthogEvent(POSTHOG_EVENTS.exportLimitsCheckRetried, { project_id: projectId });
    void newDownloadStore.retryLimits();
  };

  const submissionBlock = getSubmissionBlock({ lastSubmission, projectId, now: cooldownNow });
  const submitBlockedReason = submissionBlock ? labels.submitCooldown(submissionBlock.secondsRemaining) : undefined;

  const customDataPending =
    !exportCustomDataResolved || (exportCustomDataEnabled && !customDataResolved && !customDataError);
  const siteDataPending =
    potholeFieldNoteExportEnabled && siteDataLayerEnabled && (!siteDataGeoItems || !currentOrganization);

  const submitExtract = async (ccEmails?: string[]) => {
    if (!project || !projectionId || !currentOrganizationId || formats.length === 0) return;
    // The cooldown only begins once the response lands, so the in-flight flag covers the round trip
    // — without it a second Export → Acknowledge inside that window starts a second FME run. A ref
    // rather than the state flag: two clicks in one tick both read the pre-render state value.
    if (submitInFlight.current || submissionBlock) return;

    submitInFlight.current = true;
    setIsSubmitting(true);

    try {
      const response = await submitProjectExtract(
        buildProjectExtractRequest({
          projectId: project.id,
          organizationId: currentOrganizationId,
          selectedLayerIds,
          formats,
          projectionId,
          extractTemplateId,
          ccEmails,
        }),
      );

      downloadStore.recordSubmission(project.id);

      const dropped = describeDroppedLayers(response.droppedLayerIds, (layerId) => TOCLabelMap.get(layerId));

      const submitToast = dropped ? `${labels.submitToast} ${labels.droppedLayersNotice(dropped)}` : labels.submitToast;

      notifyExportQueued({
        message: submitToast,
        ccEmails,
      });

      captureTypedPosthogEvent(
        POSTHOG_EVENTS.projectExportInitiated,
        buildExportInitiatedProperties({
          projectId: project.id,
          organizationId: currentOrganizationId,
          formats,
          selectedLayerIds,
          submissionId: response.submissionId,
          extractTemplateId,
          extractTemplateName: templates.find((t) => t.extractTemplateId === extractTemplateId)?.name,
          projectionId,
          projectionName: projections.find((p) => p.id === projectionId)?.label,
          ccRecipientCount: ccEmails?.length ?? 0,
          entryPoint,
        }),
      );

      // With the history inline below the form, the new run shows up where the user already is.
      if (multiFormatEnabled) {
        downloadStore.setAwaitingSubmission(response.submissionId ?? null);
      } else {
        navigate({
          to: '/projects/$projectId/$tabId',
          params: { projectId: project.id, tabId: EXPORT_HISTORY_TAB_ID },
        });
      }
    } catch (err) {
      captureTypedPosthogEvent(
        POSTHOG_EVENTS.projectExportFailed,
        buildExportFailedProperties({
          projectId: project.id,
          organizationId: currentOrganizationId,
          reason: getReasonForError(err),
        }),
      );
      Notify.error({
        title: '⚠️',
        message: labels.submitErrorToast,
      });
      return false;
    } finally {
      submitInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const startExtract = () => {
    if (submitInFlight.current || submissionBlock) return;
    setConfirmOpen(true);
  };

  if (formLoading) {
    return <SkeletonSpinnerCentered />;
  }

  if (!isDownloadEnabled) {
    return (
      <div className="text-sm">
        <div>{labels.notEntitled}</div>
        <div className="my-4">
          <Link
            to="/projects/$projectId/$tabId"
            params={{ projectId, tabId: 'details' }}
            className="text-blue-50 underline hover:text-blue-60"
          >
            Upgrade
          </Link>{' '}
          {labels.notEntitledUpgrade}
        </div>
      </div>
    );
  }

  const guideLink = multiFormatEnabled && (
    <a
      className="flex w-fit items-center gap-1 text-xs text-blue-50 underline hover:text-blue-60"
      data-testid="extract-download-guide-link"
      href="https://civilgrid.help.usepylon.com/articles/9829161648-download-guide-for-dwg-dgn-shp-kmz"
      rel="noreferrer"
      target="_blank"
      onClick={() =>
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectExportGuideOpened, {
          project_id: projectId,
          organization_id: currentOrganizationId || undefined,
        })
      }
    >
      Review the guide
      <Icon icon="externalLink" />
    </a>
  );

  return (
    <div className="flex w-full flex-auto">
      <div ref={containerDiv} className="flex min-w-0 flex-col space-y-4 text-lg">
        {!multiFormatEnabled && (
          <div className="flex flex-row items-center justify-between space-x-2">
            <div className="flex flex-grow flex-row justify-end space-x-2">
              <Button variant="secondary" size="small" data-testid="extract-download-guide-button">
                <a className="rounded-sm text-primary " href="/Download Guide.pdf" rel="noreferrer" target="_blank">
                  Guide
                </a>
              </Button>
              <Button
                variant="secondary"
                size="small"
                data-testid="extract-download-history-button"
                onClick={() =>
                  navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_HISTORY_TAB_ID } })
                }
              >
                {labels.historyButton}
              </Button>
            </div>
          </div>
        )}

        <div className="text-xs">{labels.formIntro}</div>

        {pickerV2Enabled ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 whitespace-nowrap text-xs" data-testid="extract-download-area">
              <span className="font-noto font-medium text-grey-70">{labels.areaToBeExported}</span>
              <span className="font-noto-mono font-semibold text-secondary-dark">{formatAreaAcres(downloadArea)}</span>
            </div>
            {guideLink}
          </div>
        ) : (
          guideLink
        )}

        {pickerV2Enabled && lastRun && (
          <Button
            variant="secondary"
            className="w-full"
            data-testid="extract-rebuild-last-export-button"
            disabled={isCheckingLayerAssets || isLoadingProjections || customDataPending || siteDataPending}
            onClick={handleRebuildLastExport}
          >
            {labels.rebuildLastButton}
          </Button>
        )}

        <div className="flex flex-col" data-testid="extract-download-format-select">
          <div className="text-xs font-noto">File Format</div>
          <FormatPicker formats={formats} onChange={setFormats} multiple={multiFormatEnabled} />
        </div>

        {isTemplateFieldVisible(multiFormatEnabled, templates.length) && (
          <div className="flex flex-col" data-testid="extract-download-template-select">
            <div className="text-xs font-noto">Extract Template</div>
            <Select
              aria-label="Extract Template"
              value={extractTemplateId ?? DEFAULT_TEMPLATE_OPTION}
              onValueChange={(value) => setExtractTemplateId(templateIdFromOption(value))}
              options={templatePickerOptions(templates)}
              data-testid="extract-download-template-picker"
            />
          </div>
        )}

        <div className="flex flex-col">
          <div className="flex flex-row items-center gap-1">
            {pickerV2Enabled ? (
              <label htmlFor={LAYER_SEARCH_INPUT_ID} className="text-xs font-noto">
                {labels.layerPickerLabel}
              </label>
            ) : (
              <div className="text-xs font-noto">{labels.layerPickerLabel}</div>
            )}
            <InfoTooltip
              label={`About ${labels.layerPickerLabel}`}
              text={
                pickerV2Enabled ? (
                  <>
                    Layers within the project area that are selected below will be exported and sent via email. Only
                    layers with assets inside the project boundary are available to export.
                  </>
                ) : (
                  <>
                    The selected layers will be exported for the full project boundary.
                    <br />
                    Only layers with assets inside the boundary are listed.
                  </>
                )
              }
            />
          </div>

          {pickerV2Enabled ? (
            <div className="mt-1" data-testid="extract-download-layers-inline">
              <DownloadLayerPicker
                currentObjectArea={downloadArea}
                extraSections={extraSections.length > 0 ? extraSections : undefined}
                labels={labels}
              />
            </div>
          ) : (
            <Popover
              usePortal
              trigger={
                <div
                  data-testid="extract-download-layers-trigger"
                  className="flex w-full cursor-pointer items-center justify-between border border-grey-30 rounded-sm p-1.5 text-xs outline-none focus:border-blue-500"
                >
                  <div>{getSelectedLayersText(selectedLayerIds)}</div>
                  <Icon icon="chevronDown" />
                </div>
              }
            >
              {(_closePopover) => (
                <div
                  className="z-100 flex min-h-0 min-w-100 flex-col border bg-white text-sm border-grey-30 rounded-sm"
                  style={{ maxHeight: 'var(--radix-popover-content-available-height)', overflow: 'hidden' }}
                >
                  <DownloadLayerPicker
                    currentObjectArea={downloadArea}
                    extraSections={extraSections.length > 0 ? extraSections : undefined}
                    labels={labels}
                  />
                </div>
              )}
            </Popover>
          )}

          {selectedLayerIds.size === 0 && (
            <div className="mt-2">
              <NoLayersWarning labels={labels} />
            </div>
          )}
        </div>

        {isProjectionFieldVisible(multiFormatEnabled, formats) && (
          <div className="flex flex-col space-y-2" data-testid="extract-download-projection-select">
            <div className="text-lg">Projection</div>
            <ComboBox
              aria-label="Projection"
              placeholder="Select..."
              data-testid="extract-download-projection-picker"
              disabled={!isProjectionPickerEnabled(formats)}
              value={projectionSelectValue(formats, projectionId)}
              onValueChange={setProjectionId}
              allowDeselect={false}
              triggerClassName="w-full"
              options={projections.map((projection) => ({
                label: projection.label,
                value: projection.id,
                description: getProjectionDescription(projection),
              }))}
            />
          </div>
        )}

        {asyncLimits && (
          <ExportLimitsStatus
            status={limitsStatus}
            checkingLabel={labels.limitsChecking}
            unavailableTitle={labels.limitsUnavailableTitle}
            unavailableDescription={labels.limitsUnavailableBody}
            retryLabel={labels.limitsRetry}
            onRetry={retryLimits}
            data-testid="export-limits-status"
            retryButtonTestId="export-limits-retry"
          />
        )}

        <div className="flex flex-row space-x-2">
          {!showManualRequestFallback && (
            <DownloadButton
              onClick={startExtract}
              isDisabled={isDownloadDisabled || isSubmitting || !!submissionBlock}
              isSubmitting={isSubmitting}
              disabledReason={submitBlockedReason}
            />
          )}
          {showManualRequestFallback && (
            <SubmitRequestDialog
              projectId={projectId}
              projection={selectedProjectionLabel}
              fileFormat={formatSelectionLabel(formats)}
              downloadArea={downloadArea}
              source="extract_download_request"
              labels={labels}
              formats={formats}
              projectionId={projectionId}
              extractTemplateId={extractTemplateId}
              entryPoint={entryPoint}
            />
          )}
        </div>

        {!isDownloadDisabled && (
          <div className="pb-4 text-2xs">
            <ConfidentialityNotice leadIn={labels.confidentialityNote} data-testid="extract-confidentiality-notice" />
          </div>
        )}

        <RestrictionInfo downloadArea={downloadArea} labels={labels} showNoLayersWarning={false} />

        {multiFormatEnabled && <ExportHistory projectId={projectId} onInitialLoad={reportExportTabLoaded} />}

        <SendEmailModal
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={labels.confirmModalTitle}
          testIdPrefix="extract-download-acknowledge"
          submitLabel={labels.confirmButton}
          acknowledgement={<DataUseAcknowledgement />}
          onSend={(ccEmails) => submitExtract(ccEmails.length > 0 ? ccEmails : undefined)}
        />
      </div>
    </div>
  );
}

interface DownloadButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isDisabled: boolean;
  /** Renders the spinner while the submit request is open. */
  isSubmitting?: boolean;
  /** Shown as a tooltip while disabled. */
  disabledReason?: string;
}

/** Primary submit button, which opens the shared export confirmation dialog. */
function DownloadButton({ isDisabled, isSubmitting = false, disabledReason, ...props }: DownloadButtonProps) {
  const labels = useExportLabels();

  return (
    <Button
      {...props}
      data-testid="extract-download-download-button"
      disabled={isDisabled}
      loading={isSubmitting}
      tooltip={disabledReason}
    >
      <div className="flex flex-row items-center space-x-1">
        <Icon icon="download" />
        <div>{labels.submitButton}</div>
      </div>
    </Button>
  );
}
